import React, { createContext, useState, useContext } from 'react';
import { kundliService, horoscopeService } from '../services/api';

// Create astrology context
const AstrologyContext = createContext();

// Astrology provider component
export const AstrologyProvider = ({ children }) => {
  const [kundlis, setKundlis] = useState([]);
  const [currentKundli, setCurrentKundli] = useState(null);
  const [horoscope, setHoroscope] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Generate a new kundli
  const generateKundli = async (kundliData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await kundliService.generateKundli(kundliData);
      setCurrentKundli(response.data.kundli);
      return response.data.kundli;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate kundli');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get all kundlis for the user
  const getUserKundlis = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await kundliService.getKundlis();
      setKundlis(response.data.kundlis);
      return response.data.kundlis;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch kundlis');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get kundli details
  const getKundliDetails = async (id) => {
    try {
      setLoading(true);
      setError(null);
      const response = await kundliService.getKundli(id);
      setCurrentKundli(response.data.kundli);
      return response.data.kundli;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch kundli details');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get horoscope for a zodiac sign
  const getHoroscope = async (sign, type = 'daily') => {
    try {
      setLoading(true);
      setError(null);
      const response = await horoscopeService.getHoroscope(sign, type);
      setHoroscope(response.data.horoscope);
      return response.data.horoscope;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch horoscope');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Context value
  const value = {
    kundlis,
    currentKundli,
    horoscope,
    loading,
    error,
    generateKundli,
    getUserKundlis,
    getKundliDetails,
    getHoroscope,
  };

  return <AstrologyContext.Provider value={value}>{children}</AstrologyContext.Provider>;
};

// Custom hook to use astrology context
export const useAstrology = () => {
  const context = useContext(AstrologyContext);
  if (!context) {
    throw new Error('useAstrology must be used within an AstrologyProvider');
  }
  return context;
};
